import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { or } from 'tio-ui/utilities';
import Modal from 'tio-reimagined/components/modal';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import { PhInfo } from 'ember-phosphor-icons';
import { t } from 'ember-intl';

interface TioConfirmAlertSignature {
  Args: {
    cancelButtonText?: string;
    confirmButtonText?: string;
    disabled?: boolean;
    footerText?: string;
    hideCancel?: boolean;
    hideClose?: boolean;
    isDestructive?: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    show: boolean;
    renderInPlace?: boolean;
  };
  Blocks: {
    default: [];
  };
}

export default class TioConfirmAlertComponent extends Component<TioConfirmAlertSignature> {
  @tracked error: { message: string } | null = null;
  @tracked isRunning = false;

  confirm = async () => {
    this.error = null;
    this.isRunning = true;
    try {
      await this.args.onConfirm?.();
    } catch (e) {
      console.error(e);
      this.error = e;
    } finally {
      this.isRunning = false;
    }
  };

  cancel = () => {
    this.args.onCancel?.();
    this.error = null;
  };

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{this.cancel}}
      class="min-h-64"
      @ariaLabel={{t "common.aria_labels.confirm_action"}}
    >
      <:body>
        {{yield}}
        {{#if this.error}}
          <div class="px-6 py-1 text-xs text-error-500">
            {{or this.error.message "An unknown error occurred"}}
          </div>
        {{/if}}
      </:body>
      <:footer>
        {{#unless @hideCancel}}
          <Button
            @appearance="outlined"
            @intent="danger"
            {{on "click" this.cancel}}
            class="w-1/2 md:w-48"
            data-test-modal-cancel
          >
            {{or @cancelButtonText "Cancel"}}
          </Button>
        {{/unless}}
        <Button
          {{on "click" this.confirm}}
          class="ml-4 w-1/2 md:w-48"
          @intent={{if @isDestructive "danger" "primary"}}
          @isRunning={{this.isRunning}}
          @isDisabled={{@disabled}}
          data-test-modal-submit
        >
          {{or @confirmButtonText "Confirm"}}
        </Button>
        {{#if @footerText}}
          <div class="flex items-center justify-center mt-2">
            <PhInfo @size="32" class="text-ocean-600 mr-1" />
            <p class="text-sm text-tio-gray-600">{{@footerText}}</p>
          </div>
        {{/if}}
      </:footer>
    </Modal>
  </template>
}
